import { useEffect, useMemo, useState } from "react";
import "./theme.css";
import {
  codesOfArticle,
  exemptionGroups,
  loadExemptionList,
  searchExemptions,
  groupCodesByClause,
  clauseText,
  clauseLabel,
  clauseHeadIsRedundant,
  codeDistinctPart,
  codeExtraNote,
  narrowestText,
  shortRef,
  sortCodesForDisplay,
  ruleParagraphsFor,
  sameAsClauseHead,
} from "./exemptionSearch";

function readArt() {
  const p = new URLSearchParams(window.location.search);
  return parseInt(p.get("art") || "", 10) || null;
}
function readGroup() {
  return new URLSearchParams(window.location.search).get("grp") || null;
}
function readQuery() {
  return new URLSearchParams(window.location.search).get("eq") || "";
}
// 조문(art) 또는 그 밖의 근거 묶음(grp) 하나를 고른다 — 검색어(eq)는 주소에 남아 뒤로 가기로 돌아온다.
function goTarget({ art = null, grp = null }) {
  const p = new URLSearchParams(window.location.search);
  p.set("audit", "exemption");
  if (art) p.set("art", art);
  else p.delete("art");
  if (grp) p.set("grp", grp);
  else p.delete("grp");
  window.history.pushState({}, "", "/?" + p.toString());
  window.dispatchEvent(new PopStateEvent("popstate"));
}
const goArt = (art) => goTarget({ art });

const codeTh = {
  textAlign: "left",
  padding: "6px 8px",
  fontSize: 11.5,
  fontWeight: 700,
  color: "var(--audit-text-muted)",
  borderBottom: "1px solid var(--audit-border-strong)",
  whiteSpace: "nowrap",
};
const codeTd = {
  padding: "6px 8px",
  fontSize: 12.5,
  verticalAlign: "top",
  borderBottom: "1px solid var(--audit-border)",
};

// "108" → 제108조, "48의2" → 제48조의2
const artLabel = (art) => {
  const [jo, branch] = String(art).split("의");
  return branch ? `제${jo}조의${branch}` : `제${jo}조`;
};
const lawHref = (lawName, art) => encodeURI(`https://www.law.go.kr/법령/${lawName}/${artLabel(art)}`);
// 행정규칙(고시·훈령)은 법령과 주소 체계가 다르다 — 조문 번호 없이 이름만.
const ruleHref = (name) => encodeURI(`https://www.law.go.kr/행정규칙/${name}`);
// 법 조문에 딸린 시행규칙 중 본문이 있는 것
const ruleBodiesOf = (law) =>
  ((law && law.related) || []).filter((r) => (r.body || []).length && /시행규칙/.test(r.law));
const lawChip = {
  fontSize: 12.5,
  padding: "3px 9px",
  border: "1px solid var(--audit-border)",
  borderRadius: 12,
  color: "var(--audit-accent)",
  textDecoration: "none",
  whiteSpace: "nowrap",
};

// 조문의 관련법령(시행령·시행규칙 조문) 링크와 조문 원문 — law.go.kr Open API 조문 구조 그대로.
// 공식 연간 부호표에 없어 다른 원천에서 보탠 부호. 그 원천이 주지 않는 열은 빈칸이 아니라 "확인 필요"로
// 적는다 — "—"는 "대상 아님"으로 읽혀, 모르는 것을 아는 것처럼 말하게 된다.
const SUPPLEMENT_SOURCES = {
  office: "관세청 연간 부호표(2026)에는 없고, 통관 프로그램 기초자료에서 보탠 부호입니다.",
  unipass: "관세청 연간 부호표(2026)에는 없고, 유니패스 현행 부호조회에서 보탠 부호입니다.",
};
// 적용기간은 두 원천 모두 주지 않는다(그 열은 내국세 부호 표에만 있어 관세감면 표에는 안 보인다).
// 사후관리·농특세는 사무소 기초자료에는 있고 유니패스에는 없다.
const UNKNOWN_PERIOD = ["office", "unipass"];
const UNKNOWN_MGMT = ["unipass"];

function Unknown({ why }) {
  return (
    <span data-testid="exemption-unknown" style={{ color: "var(--audit-text-muted)" }} title={why}>
      확인 필요
    </span>
  );
}

export function ExemptionLawBlock({ law, article, sources }) {
  if (!law || !article) return null;
  const effective = sources && sources["관세법"] && sources["관세법"].effective;
  return (
    <div data-testid="exemption-law-block" style={{ marginTop: 14 }}>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ fontSize: 12.5, fontWeight: 700 }}>관련법령</span>
        <a href={lawHref("관세법", article)} target="_blank" rel="noreferrer" style={lawChip}>
          관세법 제{article}조 ↗
        </a>
        {(law.related || []).map((r) => (
          <a
            key={`${r.law}-${r.art}`}
            href={lawHref(r.law, r.art)}
            target="_blank"
            rel="noreferrer"
            title={r.title}
            style={lawChip}
          >
            {r.law.replace("관세법 ", "")} {artLabel(r.art)} ↗
          </a>
        ))}
        {/* 그 조문이 굴러가는 데 읽어야 하는 고시·훈령 — 본문이 그 조문을 인용하는 것만 실린다(문의 #95) */}
        {(law.admrules || []).map((r) => (
          <a
            key={r.id}
            data-testid="exemption-admrule"
            href={ruleHref(r.law)}
            target="_blank"
            rel="noreferrer"
            title={`${r.kind} · ${r.ministry}${r.effective ? ` · 시행 ${r.effective.replace(/(\d{4})(\d{2})(\d{2})/, "$1-$2-$3")}` : ""}`}
            style={lawChip}
          >
            {r.law} ↗
          </a>
        ))}
      </div>
      <details style={{ marginTop: 8 }}>
        <summary style={{ cursor: "pointer", fontSize: 12.5, fontWeight: 600 }}>
          관세법 제{article}조 원문{effective ? ` (시행 ${effective.slice(0, 4)}-${effective.slice(4, 6)}-${effective.slice(6)})` : ""}
        </summary>
        <div style={{ fontSize: 13, lineHeight: 1.75, marginTop: 6 }}>
          {(law.body || []).map((p, i) => (
            <div key={i} style={{ marginBottom: 6 }}>
              {p.text && <div>{p.text}</div>}
              {(p.items || []).map((it, k) => (
                <div key={k} style={{ paddingLeft: 16 }}>
                  {it}
                </div>
              ))}
            </div>
          ))}
        </div>
      </details>
      {/* 관세법 각 호는 "재정경제부령으로 정하는 기관·물품"이라고만 하고, 그 기관이 누구인지는
          시행규칙에 있다(문의 #100). 링크로 나가야만 보이면 조문을 읽어도 답이 안 나온다. */}
      {(law.related || []).filter((r) => (r.body || []).length && /시행규칙/.test(r.law)).map((r) => (
        <details key={`b-${r.law}-${r.art}`} data-testid="exemption-rule-body" style={{ marginTop: 6 }}>
          <summary style={{ cursor: "pointer", fontSize: 12.5, fontWeight: 600 }}>
            {r.law.replace("관세법 ", "")} {artLabel(r.art)} 원문{r.title ? ` (${r.title})` : ""}
          </summary>
          <div style={{ fontSize: 13, lineHeight: 1.75, marginTop: 6 }}>
            {r.body.map((p, i) => (
              <div key={i} style={{ marginBottom: 6 }}>
                {p.text && <div>{p.text}</div>}
                {(p.items || []).map((it, k) => (
                  <div key={k} style={{ paddingLeft: 16 }}>
                    {it}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </details>
      ))}
    </div>
  );
}

// 관세를 감면받으면 부가세는 어떻게 되는가. 공식 부호표에는 둘을 잇는 표가 없고, 부가가치세법
// 제27조가 스스로 관세 감면을 참조한다(제6·9호 「관세가 면제되는」, 제12·13호 「관세가 감면되는」,
// 제15호 「그 밖에 관세가 무세이거나 감면되는」). 그 조문을 따라 이은 **후보**를 보인다 — 판정이 아니다.
// 율은 따로 싣지 않는다. 제27조제12·13·15호는 관세가 경감되면 그 경감 비율만큼만 면제한다고 적는다.
export function VatCell({ code }) {
  const c = code || {};
  if (c.vat_code) {
    return (
      <td data-testid="exemption-vat" style={{ ...codeTd, whiteSpace: "nowrap" }}>
        <span
          style={{ fontFamily: "var(--audit-mono, monospace)", fontWeight: 600 }}
          title={[c.vat_content, c.vat_basis && `근거: ${c.vat_basis}`].filter(Boolean).join("\n")}
        >
          {c.vat_code}
        </span>
      </td>
    );
  }
  if (c.vat_status === "none") {
    return (
      <td data-testid="exemption-vat" style={{ ...codeTd, whiteSpace: "nowrap", color: "var(--audit-text-muted)" }}>
        <span title={c.vat_basis || "부가가치세법 제27조에 해당 호가 없습니다."}>과세</span>
      </td>
    );
  }
  return (
    <td data-testid="exemption-vat" style={{ ...codeTd, whiteSpace: "nowrap" }}>
      <Unknown why={c.vat_basis || "이 부호에 대응하는 부가세 면세 조문을 찾지 못했습니다."} />
    </td>
  );
}

// 표 위 거르기 — 부호가 수십 개인 조문(제90조 74개)에서 필요하다. 조건에 맞는 줄만 남기고,
// 고른 칩이 없으면 전부 보인다. 거르기는 표시일 뿐 값을 바꾸지 않는다.
const CODE_FILTERS = [
  { key: "post", label: "사후관리 대상", match: (c) => c.post_mgmt === true },
  { key: "vat", label: "부가세 면세", match: (c) => !!c.vat_code },
  { key: "vatask", label: "부가세 확인 필요", match: (c) => !c.vat_code && c.vat_status !== "none" },
];

// 신고서 관세감면·분납 부호 표 — 관세청 통계부호 값 그대로(문의 #76). 검색에 걸린 줄은 굵게.
export function ExemptionCodeTable({ codes: allCodes, hitCodes, article, articleBody, ruleBodies }) {
  const [filter, setFilter] = useState(null);
  if (!allCodes || allCodes.length === 0) return null;
  const active = CODE_FILTERS.find((f) => f.key === filter);
  const codes = active ? allCodes.filter(active.match) : allCodes;
  const hit = new Set(hitCodes || []);
  const anyInstallment = codes.some((c) => c.installment_months);
  // 내국세 부호는 감면율·사후관리·농특세 값이 없다 — 부호·내용·적용기간만.
  const taxOnly = codes.every((c) => c.tax);
  // 관세감면 부호는 조문의 항·호로 묶는다. 내국세 부호는 관세법 조문이 없어 한 묶음으로 남는다.
  // 묶음 안에서 S·H(기관을 묶은 부호)를 위로 올린다(문의 #101).
  const groups = (article ? groupCodesByClause(codes, article) : [{ key: "", clause: null, codes }])
    .map((g) => ({ ...g, codes: sortCodesForDisplay(g.codes) }));
  return (
    <div data-testid="exemption-code-table" style={{ marginTop: 16 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap", margin: "0 0 6px" }}>
        <h3 style={{ fontSize: 13.5, fontWeight: 800, margin: 0 }}>
          {taxOnly ? "감면·면세 부호" : "관세감면·분납 부호"} {codes.length}개
          {active && <span style={{ fontWeight: 400, color: "var(--audit-text-muted)" }}> / {allCodes.length}</span>}
        </h3>
        {!taxOnly && allCodes.length > 6 && (
          <div data-testid="exemption-filters" style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
            {CODE_FILTERS.map((f) => {
              const n = allCodes.filter(f.match).length;
              if (!n || n === allCodes.length) return null;   // 아무것도 못 거르는 칩은 그리지 않는다
              const on = filter === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setFilter(on ? null : f.key)}
                  style={{
                    font: "inherit", fontSize: 11.5, padding: "3px 9px", borderRadius: 999, cursor: "pointer",
                    border: `1px solid ${on ? "var(--audit-accent)" : "var(--audit-border)"}`,
                    background: on ? "var(--audit-accent-soft, #eaf1fb)" : "transparent",
                    color: on ? "var(--audit-accent)" : "var(--audit-text-muted)",
                    fontWeight: on ? 700 : 400,
                  }}
                >
                  {f.label} {n}
                </button>
              );
            })}
          </div>
        )}
      </div>
      {/* 빈칸의 뜻을 표 밖에서 한 번 밝힌다 — 「해당 없음」과 「원천이 값을 주지 않음」은 다른 말이다. */}
      {!taxOnly && (
        <div style={{ fontSize: 11, color: "var(--audit-text-faint)", margin: "0 0 5px", lineHeight: 1.6 }}>
          — 해당 없음 · <span style={{ borderBottom: "1px dotted var(--audit-border-strong)" }}>확인 필요</span> 원천이 값을 주지 않음
        </div>
      )}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={codeTh}>부호</th>
              {!taxOnly && <th style={codeTh}>근거 호</th>}
              <th style={codeTh}>내용</th>
              {taxOnly ? (
                <th style={codeTh}>적용기간</th>
              ) : (
                <>
                  <th style={{ ...codeTh, textAlign: "right" }}>감면율</th>
                  {anyInstallment && <th style={codeTh}>분납</th>}
                  <th style={codeTh}>사후관리</th>
                  <th style={codeTh}>농특세</th>
                  <th style={codeTh}>부가세</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {/* 원문은 호로 나뉘는데 표가 평평하면 어느 부호가 몇 호에 붙는지 한 줄씩 읽어야 한다(문의 #98).
                부호 내용에 이미 근거가 적혀 있으므로 그것으로 묶고, 묶음 머리에 그 호의 원문을 단다. */}
            {groups.map((g) => {
              const headText = clauseText(articleBody, g.clause);
              // 법이 "부령으로 정하는"이라고만 한 호는 그 부령(시행규칙 항)을 머리글 밑에 붙인다(문의 #100·#118).
              const rules = ruleParagraphsFor(ruleBodies, g.clause, article, g.codes);
              const showHead = groups.length > 1 && (!clauseHeadIsRedundant(g, articleBody) || rules.length > 0);
              return [
              // 머리글이 부호 줄과 같은 말을 하면 걷는다 — 부호 줄에 호 이름이 배지로 붙는다.
              showHead && (
                <tr key={`h-${g.key}`} data-testid="exemption-clause-head">
                  <td
                    colSpan={12}
                    style={{
                      padding: "10px 8px 4px",
                      borderBottom: "1px solid var(--audit-border)",
                      verticalAlign: "top",
                    }}
                  >
                    {/* 법인지 시행규칙인지 밝힌다 — 이 줄은 법 조문의 호다(문의 #120) */}
                    <span style={{ fontSize: 12.5, fontWeight: 800, whiteSpace: "nowrap" }}>
                      {g.clause && article ? `관세법 제${article}조 ` : ""}{clauseLabel(g.clause)}
                    </span>
                    <span style={{ fontSize: 11.5, color: "var(--audit-text-faint)", marginLeft: 8 }}>
                      부호 {g.codes.length}개
                    </span>
                    {headText && (
                      <div style={{ fontSize: 12, color: "var(--audit-text-muted)", marginTop: 3, lineHeight: 1.6 }}>
                        {headText}
                      </div>
                    )}
                    {rules.map((r) => (
                      <div
                        key={`${r.law}-${r.art}-${r.hang}`}
                        data-testid="exemption-clause-rule"
                        style={{ fontSize: 12, marginTop: 5, paddingLeft: 8, borderLeft: "2px solid var(--audit-border-strong)", lineHeight: 1.6 }}
                      >
                        <div style={{ fontWeight: 700 }}>
                          {r.law.replace("관세법 ", "")} 제{r.art}조제{r.hang}항
                        </div>
                        {r.para.text && <div>{r.para.text}</div>}
                        {(r.para.items || []).map((it, k) => (
                          <div key={k} style={{ paddingLeft: 12 }}>{it}</div>
                        ))}
                      </div>
                    ))}
                  </td>
                </tr>
              ),
              ...g.codes.map((c) => {
              const on = hit.has(c.code);
              return (
                <tr key={c.code} data-hit={on ? "1" : undefined}>
                  <td
                    style={{
                      ...codeTd,
                      fontFamily: "var(--audit-mono, monospace)",
                      whiteSpace: "nowrap",
                      fontWeight: on ? 800 : 600,
                    }}
                  >
                    {c.code}
                    {/* 공식 연간 부호표에 없고 다른 원천에서 보탠 부호(그해 개정 신설분) */}
                    {SUPPLEMENT_SOURCES[c.source] && (
                      <div
                        data-testid="exemption-code-office"
                        style={{ fontSize: 10.5, fontWeight: 400, color: "var(--audit-text-muted)" }}
                        title={SUPPLEMENT_SOURCES[c.source]}
                      >
                        부호표 미수록
                      </div>
                    )}
                  </td>
                  {!taxOnly && (
                    <td
                      data-testid="exemption-clause-ref"
                      style={{ ...codeTd, whiteSpace: "nowrap", fontSize: 11.5, color: "var(--audit-text-muted)" }}
                    >
                      {shortRef((narrowestText(c) || {}).ref) || "—"}
                    </td>
                  )}
                  <td style={{ ...codeTd, fontWeight: on ? 700 : 400, lineHeight: 1.55 }}>
                    {/* 가장 좁은 조문 하나만 보인다. 넓은 쪽은 묶음 머리가 말하거나(제90조) 이것과
                        같은 글이다(제88조 제1항) — 둘 다 그리면 같은 문장을 두 번 읽게 된다.
                        조문 이름은 「근거 호」 열이 말하므로 줄 안에서는 뺀다. */}
                    {/* 머리글이 같은 글을 이미 보였으면 줄에서는 뺀다(문의 #118) */}
                    {[narrowestText(c)].filter(Boolean).filter((t) => !(showHead && sameAsClauseHead(t.text, headText))).map((t) => (
                      <div key={t.ref} data-testid="exemption-law-text" style={{ marginBottom: 3 }}>
                        {t.text}
                        {/* 별표의 세부 품목 — 길어서 접어 둔다. 줄바꿈 복원은 추정이라 원문 PDF 를 함께 건다. */}
                        {t.pdf && (
                          <a
                            href={t.pdf}
                            target="_blank"
                            rel="noreferrer"
                            style={{ marginLeft: 6, fontSize: 11, fontWeight: 400, color: "var(--audit-accent)", whiteSpace: "nowrap" }}
                          >
                            별표 원문 PDF ↗
                          </a>
                        )}
                        {Array.isArray(t.items) && t.items.length > 0 && (
                          <details data-testid="exemption-annex-items" style={{ fontWeight: 400, marginTop: 2 }}>
                            <summary style={{ cursor: "pointer", fontSize: 12, color: "var(--audit-text-muted)" }}>
                              세부 품목 {t.items.length}줄
                            </summary>
                            {t.items.map((line, k) => (
                              <div key={k} style={{ paddingLeft: 12, fontSize: 12.5 }}>
                                {line}
                              </div>
                            ))}
                          </details>
                        )}
                      </div>
                    ))}
                    {/* 부호표 문자열은 대부분 조문 참조뿐이라(「관세법 제88조제1항제1호 해당물품」)
                        「근거 호」 열과 겹친다. 조문 참조를 걷고도 남는 말이 있을 때만 보인다
                        — 「…해당물품중 공공의료기관 및 학교부설의료기관에서 사용할 물품」의 뒷말. */}
                    {(() => {
                      // 조문 원문이 붙은 부호는 「근거 호」 열이 참조를 말하므로 남는 말만 보인다.
                      // 원문이 없는 부호는 이 줄이 전부이므로 종전대로 둔다 — 지워서 빈칸을 만들지 않는다.
                      const hasText = !!(c.texts && c.texts.length);
                      const note = hasText
                        ? codeExtraNote(c.content)
                        : groups.length > 1 ? codeDistinctPart(c.content) : c.content;
                      if (!note) {
                        // 머리글과 같은 글을 걷어 줄이 비면 비었다고 말한다 — 빈칸은 누락으로 읽힌다.
                        const t = narrowestText(c);
                        return showHead && t && sameAsClauseHead(t.text, headText)
                          ? <div style={{ fontSize: 11.5, color: "var(--audit-text-muted)" }}>위 호 전체</div>
                          : null;
                      }
                      return (
                        <div style={hasText ? { fontSize: 11.5, fontWeight: 400, color: "var(--audit-text-muted)" } : undefined}>
                          {note}
                        </div>
                      );
                    })()}
                  </td>
                  {taxOnly && (
                    <td style={{ ...codeTd, whiteSpace: "nowrap", color: "var(--audit-text-muted)" }}>
                      {c.from
                        ? `${c.from} ~ ${c.to || ""}`
                        : UNKNOWN_PERIOD.includes(c.source)
                          ? <Unknown why="이 부호를 가져온 원천이 적용기간을 주지 않습니다." />
                          : "—"}
                    </td>
                  )}
                  {!taxOnly && (
                  <>
                  <td style={{ ...codeTd, textAlign: "right", whiteSpace: "nowrap" }}>
                    {c.rate == null ? "—" : `${c.rate}%`}
                  </td>
                  {anyInstallment && (
                    <td style={{ ...codeTd, whiteSpace: "nowrap" }}>
                      {c.installment_months
                        ? `${c.installment_months}개월 · ${c.installment_count || "—"}회`
                        : "—"}
                    </td>
                  )}
                  <td style={{ ...codeTd, whiteSpace: "nowrap" }}>
                    {c.post_mgmt
                      ? "대상"
                      : UNKNOWN_MGMT.includes(c.source)
                        ? <Unknown why="이 부호를 가져온 원천이 사후관리 여부를 주지 않습니다." />
                        : "—"}
                  </td>
                  <td style={{ ...codeTd, whiteSpace: "nowrap" }}>
                    {c.rural_tax != null
                      ? c.rural_tax ? "과세" : "비과세"
                      : UNKNOWN_MGMT.includes(c.source)
                        ? <Unknown why="이 부호를 가져온 원천이 농특세 과세 여부를 주지 않습니다." />
                        : "—"}
                  </td>
                  <VatCell code={c} />
                  </>
                  )}
                </tr>
              );
              }),
              ];
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function ExemptionPage() {
  const [items, setItems] = useState(null);
  const [codes, setCodes] = useState([]);
  const [codesSource, setCodesSource] = useState(null);
  const [lawArticles, setLawArticles] = useState({});
  const [lawSources, setLawSources] = useState({});
  const [sel, setSel] = useState(readArt());
  const [grp, setGrp] = useState(readGroup());
  const [query, setQuery] = useState(readQuery());
  const [guide, setGuide] = useState(null); // {loading}|{text,cached}|{err}
  const [guideArt, setGuideArt] = useState(null);

  useEffect(() => {
    loadExemptionList().then((d) => {
      setItems(d.items);
      setCodes(d.codes);
      setCodesSource(d.source);
      setLawArticles(d.lawArticles || {});
      setLawSources(d.lawSources || {});
    });
  }, []);
  useEffect(() => {
    const h = () => {
      setSel(readArt());
      setGrp(readGroup());
      setQuery(readQuery());
    };
    window.addEventListener("popstate", h);
    return () => window.removeEventListener("popstate", h);
  }, []);

  const active = items?.find((a) => a.article === sel) || null;
  const groups = useMemo(() => exemptionGroups(items, codes), [items, codes]);
  const activeGroup = !active ? groups.find((g) => g.key === grp) || null : null;
  const found = useMemo(() => searchExemptions(query, items, codes), [query, items, codes]);
  const searching = query.trim().length >= 2;
  const hitArticles = new Set(found.articles.map((a) => a.article));
  const hitGroups = new Set(found.groups);
  const shownItems = searching
    ? (items || []).filter((a) => hitArticles.has(a.article))
    : items || [];
  const shownGroups = searching ? groups.filter((g) => hitGroups.has(g.key)) : groups;
  const onQuery = (value) => {
    setQuery(value);
    const p = new URLSearchParams(window.location.search);
    if (value.trim()) p.set("eq", value);
    else p.delete("eq");
    window.history.replaceState({}, "", "/?" + p.toString());
  };

  // 한 번 받은 가이드는 조문별로 들고 있는다 — 닫았다 다시 열 때 LLM 을 다시 부르지 않는다(문의 #94).
  const [guideCache, setGuideCache] = useState({});

  const loadGuide = (art) => {
    setGuideArt(art);
    const had = guideCache[art];
    if (had) {
      setGuide(had);
      return;
    }
    setGuide({ loading: true });
    fetch(`/api/onto/exemption/guide?art=${art}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => {
        const g = { text: d.guide, cached: d.cached };
        setGuideCache((prev) => ({ ...prev, [art]: g }));
        setGuide(g);
      })
      .catch(() => setGuide({ err: true }));
  };

  // 닫으면 'AI 가이드 보기' 버튼이 돌아온다. 종전에는 한 번 열면 되돌릴 길이 없었다(문의 #94).
  const closeGuide = () => {
    setGuideArt(null);
    setGuide(null);
  };

  return (
    <div
      className="audit-root"
      style={{ display: "flex", height: "100%", background: "var(--audit-bg)" }}
    >
      {/* 좌: 조문 목록 */}
      <div
        style={{
          width: 300,
          borderRight: "1px solid var(--audit-border)",
          overflow: "auto",
          padding: 12,
          flexShrink: 0,
        }}
      >
        <h2 style={{ fontSize: 15, fontWeight: 800, margin: "4px 6px 10px" }}>
          관세감면 (관세법 제88~101조)
        </h2>
        <input
          type="search"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          aria-label="관세감면 검색"
          placeholder="감면 검색 — 대사관 · 학술 · 장애인 · 부호"
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: "7px 10px",
            fontSize: 13,
            border: "1px solid var(--audit-border-strong)",
            borderRadius: 6,
            background: "var(--audit-bg)",
            color: "var(--audit-text)",
            marginBottom: 8,
            fontFamily: "inherit",
          }}
        />
        {searching && (
          <div
            data-testid="exemption-search-summary"
            style={{ fontSize: 11.5, color: "var(--audit-text-muted)", margin: "0 6px 8px" }}
          >
            {shownItems.length + shownGroups.length === 0
              ? "일치하는 감면 항목이 없습니다"
              : `조문 ${shownItems.length} · 그 밖의 근거 ${shownGroups.length} · 부호 ${found.codes.length}개 일치`}
          </div>
        )}
        {shownItems.map((a) => {
          const on = a.article === sel;
          return (
            <button
              key={a.article}
              onClick={() => goArt(a.article)}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                border: "none",
                background: on ? "var(--audit-accent-bg)" : "transparent",
                cursor: "pointer",
                borderRadius: 6,
                padding: "8px 10px",
                marginBottom: 2,
                fontFamily: "inherit",
              }}
            >
              <span
                style={{
                  fontSize: 13,
                  fontWeight: on ? 700 : 500,
                  color: "var(--audit-text)",
                }}
              >
                제{a.article}조 {a.title}
              </span>
              <span
                style={{
                  marginLeft: 6,
                  fontSize: 10.5,
                  fontWeight: 700,
                  padding: "1px 5px",
                  borderRadius: 3,
                  background: a.kind === "감면" ? "#eef2ff" : "#ecfdf5",
                  color: a.kind === "감면" ? "#4338ca" : "#047857",
                }}
              >
                {a.kind}
              </span>
              <div
                style={{
                  fontSize: 11.5,
                  color: "var(--audit-text-muted)",
                  marginTop: 3,
                }}
              >
                {a.target}
              </div>
            </button>
          );
        })}
        {[["other", "그 밖의 근거 (분할납부 · 다른 법률 · 협정)"], ["tax", "내국세 감면·면세 부호 (부가세 · 개별소비세 · 교통세 · 주세)"]].map(([section, heading]) => {
          const rows = shownGroups.filter((g) => (g.section || "other") === section);
          if (rows.length === 0) return null;
          return (
            <div key={section} data-testid={`exemption-group-section-${section}`}>
              <div
                style={{
                  fontSize: 11.5,
                  fontWeight: 700,
                  color: "var(--audit-text-muted)",
                  margin: "12px 6px 4px",
                }}
              >
                {heading}
              </div>
              {rows.map((g) => {
                const on = !active && g.key === grp;
                return (
                  <button
                    key={g.key}
                    onClick={() => goTarget({ grp: g.key })}
                    style={{
                      display: "block",
                      width: "100%",
                      textAlign: "left",
                      border: "none",
                      background: on ? "var(--audit-accent-bg)" : "transparent",
                      cursor: "pointer",
                      borderRadius: 6,
                      padding: "7px 10px",
                      marginBottom: 2,
                      fontFamily: "inherit",
                      fontSize: 13,
                      fontWeight: on ? 700 : 500,
                      color: "var(--audit-text)",
                    }}
                  >
                    {g.title}
                    <span style={{ marginLeft: 6, fontSize: 11.5, color: "var(--audit-text-muted)" }}>
                      부호 {g.codes.length}
                    </span>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* 우: 상세 */}
      <div style={{ flex: 1, overflow: "auto", padding: "18px 22px", maxWidth: 860 }}>
        {!active && activeGroup ? (
          <>
            <h2 style={{ fontSize: 17, fontWeight: 800, margin: "0 0 4px" }}>{activeGroup.title}</h2>
            <div style={{ fontSize: 12.5, color: "var(--audit-text-muted)" }}>
              신고서에 적는 관세감면·분납 부호입니다. 적용 요건은 해당 법령·협정 원문으로 확인하세요.
            </div>
            <ExemptionLawBlock
              law={lawArticles[(activeGroup.key.match(/^관세법 제(\d+)조$/) || [])[1]]}
              article={(activeGroup.key.match(/^관세법 제(\d+)조$/) || [])[1]}
              sources={lawSources}
            />
            <ExemptionCodeTable codes={activeGroup.codes} hitCodes={found.codes} />
          </>
        ) : !active ? (
          <div data-testid="exemption-intro" style={{ fontSize: 13, lineHeight: 1.75 }}>
            <h2 style={{ fontSize: 17, fontWeight: 800, margin: "0 0 8px" }}>관세감면 기본 정보</h2>
            <div style={{ color: "var(--audit-text-muted)" }}>
              관세법 제88조~제101조의 감면·면제 조문 {items ? items.length : ""}개와, 신고서에 적는 관세감면·분납
              부호 {codes.length ? `${codes.length}개` : ""}를 조문별로 봅니다. 왼쪽에서 조문을 선택하세요.
            </div>
            <a
              href="https://unipass.customs.go.kr/clip/ctensrch/openULS0401003Q.do"
              target="_blank"
              rel="noreferrer"
              style={{ ...lawChip, display: "inline-block", marginTop: 12 }}
            >
              관세청 — 관세감면 안내 ↗
            </a>
          </div>
        ) : (
          <>
            <h2 style={{ fontSize: 17, fontWeight: 800, margin: "0 0 4px" }}>
              관세법 제{active.article}조 · {active.title}
            </h2>
            <div
              style={{
                display: "flex",
                gap: 14,
                flexWrap: "wrap",
                fontSize: 13,
                margin: "8px 0 14px",
              }}
            >
              <div>
                <b>구분</b> {active.kind}
              </div>
              <div>
                <b>감면율</b> {active.rate}
              </div>
            </div>
            <div
              style={{
                padding: "10px 12px",
                border: "1px solid var(--audit-border)",
                borderRadius: 8,
                background: "var(--audit-bg-subtle)",
                fontSize: 13,
                lineHeight: 1.7,
                marginBottom: 14,
              }}
            >
              <b>대상</b> — {active.target}
              <div
                style={{
                  fontSize: 11.5,
                  color: "var(--audit-text-faint)",
                  marginTop: 6,
                }}
              >
                ※ 요약·참고용. 정확·최신 요건·별표·일몰은 아래 원문 확인.
              </div>
            </div>

            {/* AI 가이드 */}
            {guideArt !== active.article ? (
              <button
                onClick={() => loadGuide(active.article)}
                style={{
                  padding: "7px 14px",
                  border: "1px solid var(--audit-accent)",
                  borderRadius: 6,
                  background: "var(--audit-bg)",
                  color: "var(--audit-accent)",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                AI 가이드 보기
              </button>
            ) : (
              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 6,
                  }}
                >
                  <span style={{ fontSize: 12.5, fontWeight: 700 }}>AI 가이드</span>
                  <button
                    type="button"
                    data-testid="exemption-guide-close"
                    onClick={closeGuide}
                    style={{
                      marginLeft: "auto",
                      padding: "3px 10px",
                      border: "1px solid var(--audit-border-strong)",
                      borderRadius: 6,
                      background: "var(--audit-bg)",
                      color: "var(--audit-text-muted)",
                      cursor: "pointer",
                      fontFamily: "inherit",
                      fontSize: 12,
                    }}
                  >
                    닫기
                  </button>
                </div>
                {guide?.loading ? (
                  <div style={{ fontSize: 13, color: "var(--audit-text-muted)" }}>
                    가이드 생성 중…
                  </div>
                ) : guide?.err ? (
                  <div style={{ fontSize: 13, color: "var(--audit-text-muted)" }}>
                    가이드 준비 중입니다. 아래 관세청·law.go.kr 원문을 참고하세요.
                  </div>
                ) : (
                  <div
                    style={{
                      fontSize: 13.5,
                      lineHeight: 1.75,
                      whiteSpace: "pre-wrap",
                      border: "1px solid var(--audit-border)",
                      borderRadius: 8,
                      padding: "12px 14px",
                    }}
                  >
                    {guide?.text}
                  </div>
                )}
              </div>
            )}

            <ExemptionLawBlock
              law={lawArticles[String(active.article)]}
              article={String(active.article)}
              sources={lawSources}
            />
            <ExemptionCodeTable
              codes={codesOfArticle(codes, active.article)}
              hitCodes={found.codes}
              article={active.article}
              articleBody={(lawArticles[String(active.article)] || {}).body}
              ruleBodies={ruleBodiesOf(lawArticles[String(active.article)])}
            />
            {codesSource && (
              <div style={{ fontSize: 11.5, color: "var(--audit-text-faint)", marginTop: 6 }}>
                부호 출처 — 관세청 통계부호 {codesSource.replace(/\.xlsx?$/i, "")}
              </div>
            )}

          </>
        )}
      </div>
    </div>
  );
}
